import { CalendarX, Link2Off as LinkIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { GalleryPage } from '../features/portal/GalleryPage'
import { UnlockScreen } from '../features/portal/UnlockScreen'
import { usePortalSession } from '../portal/usePortalSession'
import { EmptyState, GallerySkeleton, Skeleton } from '../ui'

/**
 * Portal del cliente final.
 *
 * Layout deliberadamente distinto al del estudio: sin navegación, sin menús,
 * sin nuestra marca. El visitante llega desde un enlace de WhatsApp, casi
 * siempre en el móvil, y sólo quiere ver sus fotos.
 */
export function PortalPage() {
  const { token = '' } = useParams()
  const { session, status, error, open, markExpired } = usePortalSession(token)

  // Se intenta abrir sin contraseña: si el enlace no la pide, el cliente entra
  // directo sin ver ninguna pantalla intermedia.
  useEffect(() => {
    if (token !== '' && status === 'idle') void open(null)
  }, [token, status, open])

  /*
   | Caducada, no rota.
   |
   | Sin esta pantalla se caía en «este enlace ya no está disponible», que hace
   | pensar que uno se equivocó de enlace o que perdió sus fotos. Aquí no ha
   | fallado nada: su fotógrafo le puso fecha, las fotos siguen existiendo y la
   | salida es escribirle a él.
   */
  if (status === 'expired') {
    return (
      <EmptyState
        icon={CalendarX}
        title="Esta entrega ya caducó"
        description="Tu fotógrafo puso una fecha límite y ya pasó. Escríbele si necesitas volver a entrar: puede ampliarla y este mismo enlace volverá a funcionar."
        className="min-h-dvh justify-center"
      />
    )
  }

  if (status === 'unavailable') {
    return (
      <EmptyState
        icon={LinkIcon}
        title="Este enlace ya no está disponible"
        description="Puede haber caducado o el estudio lo ha desactivado. Escríbeles para que te envíen uno nuevo."
        className="min-h-dvh justify-center"
      />
    )
  }

  if (status === 'password_required') {
    return <UnlockScreen onSubmit={(password) => void open(password)} error={error} busy={false} />
  }

  if (session === null) {
    // Esqueleto con la forma de la galería, no una frase gris. Es un instante
    // corto, pero es el PRIMER instante: lo que ve el cliente al abrir el
    // enlace que le mandó su fotógrafo.
    return (
      <div className="min-h-dvh pt-safe-4">
        <div className="flex flex-col items-center gap-2 px-4 py-4">
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-7 w-56" />
        </div>
        <GallerySkeleton />
      </div>
    )
  }

  return <GalleryPage session={session} onExpired={markExpired} />
}
